import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { PageFade, Reveal } from "@/components/site/motion";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");
  useDocumentTitle(post?.seo_title || (post ? `${post.title} — JARE` : "Journal — JARE"));

  useEffect(() => {
    setStatus("loading");
    api
      .get(`/blog/${slug}`)
      .then((r) => {
        setPost(r.data);
        setStatus("ok");
        if (r.data.seo_description) {
          document.querySelector('meta[name="description"]')?.setAttribute("content", r.data.seo_description);
        }
      })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="mx-auto w-[92%] max-w-3xl pt-48 pb-32">
        <div className="h-8 w-2/3 animate-pulse rounded bg-white/[0.05]" />
        <div className="mt-10 aspect-[16/8] animate-pulse rounded-3xl bg-white/[0.04]" />
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <PageFade className="mx-auto flex min-h-[70vh] w-[92%] max-w-3xl flex-col items-start justify-center">
        <p className="eyebrow mb-6">404</p>
        <h1 className="font-display text-4xl font-bold text-white" data-testid="post-not-found">
          This article doesn&apos;t exist (yet).
        </h1>
        <Link to="/blog" className="btn-ghost mt-8" data-testid="back-to-blog-link">
          <ArrowLeft className="h-4 w-4" /> Back to journal
        </Link>
      </PageFade>
    );
  }

  return (
    <PageFade className="mx-auto w-[92%] max-w-3xl pt-36 pb-28">
      <Link
        to="/blog"
        data-testid="post-back-link"
        className="group inline-flex items-center gap-2 text-sm text-neutral-400 transition-colors hover:text-white"
      >
        <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" /> Journal
      </Link>

      <Reveal>
        <header className="mt-10">
          <p className="eyebrow mb-5" data-testid="post-category">{post.category}</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl" data-testid="post-title">
            {post.title}
          </h1>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-neutral-500" data-testid="post-meta">
            <span className="font-medium text-neutral-300">{post.author}</span>
            <span>·</span>
            <span>{fmtDate(post.published_at)}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> {post.reading_time} min read
            </span>
          </div>
        </header>
      </Reveal>

      {post.featured_image && (
        <Reveal delay={0.1}>
          <img
            src={mediaUrl(post.featured_image)}
            alt={post.title}
            className="mt-12 aspect-[16/8] w-full rounded-3xl border border-white/10 object-cover"
            data-testid="post-featured-image"
          />
        </Reveal>
      )}

      <Reveal delay={0.15}>
        <div className="rich-text mt-12" dangerouslySetInnerHTML={{ __html: post.body }} data-testid="post-body" />
      </Reveal>

      {post.tags?.length > 0 && (
        <Reveal>
          <div className="mt-14 flex flex-wrap gap-2 border-t border-white/[0.08] pt-8" data-testid="post-tags">
            {post.tags.map((t) => (
              <span key={t} className="rounded-full border border-white/12 px-4 py-1.5 text-xs text-neutral-400">
                #{t}
              </span>
            ))}
          </div>
        </Reveal>
      )}
    </PageFade>
  );
}
